import React, { useEffect, useRef, useState } from 'react';
import { Trans, Plural } from '@lingui/react/macro';
import { Upload, AlertCircle, CheckCircle, Clock, Video, AudioLines, Download } from 'lucide-react';
import { toast } from 'sonner';
import { SidebarMetrics } from './SidebarMetrics';

interface StickySidebarProps {
  children: React.ReactNode;
  offsetTop?: number; // pixels
  analysisData?: {
    id: string;
    transcription: string;
    improvedScript?: string | null;
    status: string;
    fillerWords: string[];
    score?: number | null;
    personalizedTips: string[];
    speechPace?: string | null;
    clarity?: string | null;
    sentiment?: string | null;
    keyPointsDetected: string[];
    intentCategory?: string | null;
    intentDescription?: string | null;
    files: Array<{
      id: string;
      name: string;
      key: string;
      size: number;
      type: string;
      duration?: number | null;
      thumbnailKey?: string | null;
      createdAt: Date;
      updatedAt: Date;
      isTemporary: boolean;
      isEncrypted: boolean;
      encryptionIv?: string | null;
      encryptionTag?: string | null;
    }>;
    // Additional fields for backward compatibility
    videoAnalysisStatus?: string;
    audioAnalysisStatus?: string;
    audioAnalyzedAt?: string | null;
    audioAnalysis?: any;
    videoAnalysis?: any;
    videoDurationSeconds?: number;
    intent?: string;
  } | null;
  audioAnalysisCompleted?: boolean;
  isAnalyzing?: boolean;
}

export default function StickySidebar({
  children,
  offsetTop = 24,
  analysisData,
  audioAnalysisCompleted,
  isAnalyzing = false,
}: StickySidebarProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isFixed, setIsFixed] = useState(false);
  const [styles, setStyles] = useState<{
    left: number;
    width: number;
    height: number;
  }>({
    left: 0,
    width: 0,
    height: 0,
  });

  const recompute = () => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) return;

    const rect = container.getBoundingClientRect();
    const containerTop = rect.top + window.scrollY; // absolute top
    const shouldFix = window.scrollY + offsetTop >= containerTop;
    const width = rect.width;
    const left = rect.left;
    const height = content.getBoundingClientRect().height;

    setIsFixed(shouldFix);
    setStyles({ left, width, height });
  };

  useEffect(() => {
    // Run only on client to avoid SSR warnings
    if (typeof window === 'undefined') return;
    recompute();
    const onScroll = () => recompute();
    const onResize = () => recompute();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll as any);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  // Determine file upload and analysis status
  const getUploadStatus = () => {
    if (!analysisData?.files) return null;
    
    const videoFiles = analysisData.files.filter(f => 
      f.key && !f.key.includes('-audio-encrypted') && 
      (f.key.includes('.mp4') || f.key.includes('.webm') || f.key.includes('.mov') || f.type.startsWith('video/'))
    );
    
    const audioFiles = analysisData.files.filter(f => 
      f.key && (f.key.includes('-audio-encrypted') || f.type.startsWith('audio/'))
    );

    const hasVideo = videoFiles.length > 0;
    const hasAudio = audioFiles.length > 0;
    const videoAnalysisStatus = analysisData.videoAnalysisStatus;
    const audioAnalysisStatus = analysisData.audioAnalysisStatus;
    const overallStatus = analysisData.status;

    // Determine video upload status
    let videoUploadStatus: 'uploading' | 'completed' | 'failed' | 'audio-only' = 'audio-only';
    if (hasVideo) {
      videoUploadStatus = 'completed';
    } else if (overallStatus === 'pending' || overallStatus === 'processing') {
      videoUploadStatus = 'uploading'; // Might still be uploading
    } else if (overallStatus === 'failed') {
      videoUploadStatus = 'failed'; // Upload failed
    }

    // Determine video analysis status  
    let videoAnalysisDisplayStatus: 'pending' | 'processing' | 'completed' | 'failed' | 'not-available' = 'not-available';
    if (hasVideo) {
      // Check if we have actual video analysis data OR explicit completed status
      if (analysisData.videoAnalysis || videoAnalysisStatus === 'completed') {
        videoAnalysisDisplayStatus = 'completed';
      } else if (videoAnalysisStatus === 'processing') {
        videoAnalysisDisplayStatus = 'processing';
      } else if (videoAnalysisStatus === 'failed') {
        videoAnalysisDisplayStatus = 'failed';
      } else {
        videoAnalysisDisplayStatus = 'pending';
      }
    }

    // Determine audio analysis status
    let audioAnalysisDisplayStatus: 'pending' | 'processing' | 'completed' | 'failed' | 'not-available' = 'not-available';
    if (hasAudio) {
      // Check if we have actual audio analysis data OR audioAnalyzedAt timestamp OR dynamic completion state
      if (analysisData.audioAnalysis || analysisData.audioAnalyzedAt || audioAnalysisCompleted) {
        // If we have audio analysis data, analysis is completed
        audioAnalysisDisplayStatus = 'completed';
      } else if (audioAnalysisStatus === 'processing') {
        audioAnalysisDisplayStatus = 'processing';
      } else if (audioAnalysisStatus === 'failed') {
        audioAnalysisDisplayStatus = 'failed';
      } else {
        // Has audio but no audioAnalysis data and no explicit status = pending
        audioAnalysisDisplayStatus = 'pending';
      }
    }

    // Debug logging can be enabled for troubleshooting
    // console.log('StickySidebar debug data:', {
    //   hasVideo,
    //   hasAudio,
    //   hasVideoAnalysis: !!analysisData.videoAnalysis,
    //   hasAudioAnalysis: !!analysisData.audioAnalysis,
    //   hasAudioAnalyzedAt: !!analysisData.audioAnalyzedAt,
    //   videoAnalysisStatus,
    //   audioAnalysisStatus,
    //   audioAnalysisCompleted,
    //   videoAnalysisDisplayStatus,
    //   audioAnalysisDisplayStatus,
    //   overallStatus,
    // });

    return {
      hasVideo,
      hasAudio,
      videoUploadStatus,
      videoAnalysisStatus: videoAnalysisDisplayStatus,
      audioAnalysisStatus: audioAnalysisDisplayStatus,
      videoFileCount: videoFiles.length,
      audioFileCount: audioFiles.length,
      totalFiles: analysisData.files.length,
    };
  };

  const uploadStatus = getUploadStatus();

  // Status display component
  const UploadStatusDisplay = () => {
    if (!uploadStatus) return null;

    return (
      <div className="mb-4 p-3 rounded-lg border border-brand-dark-blue/20 bg-gradient-to-r from-brand-dark-blue/5 to-brand-gold/5">
        <div className="text-sm font-semibold text-brand-dark-blue mb-2 flex items-center gap-2">
          <Upload className="h-4 w-4" />
          <Trans>Practice Status</Trans>
        </div>
        
        {/* Audio Status */}
        <div className="flex items-center gap-2 mb-2">
          <AudioLines className="h-3 w-3 text-brand-dark-blue/70" />
          <span className="text-xs text-brand-dark-blue/70"><Trans>Audio:</Trans></span>
          {uploadStatus.hasAudio ? (
            <div className="flex items-center gap-1">
              <CheckCircle className="h-3 w-3 text-brand-success" />
              <span className="text-xs text-brand-success font-medium"><Trans>Uploaded</Trans></span>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <AlertCircle className="h-3 w-3 text-brand-error" />
              <span className="text-xs text-brand-error font-medium"><Trans>Missing</Trans></span>
            </div>
          )}
        </div>

        {/* Video Status */}
        <div className="flex items-center gap-2 mb-2">
          <Video className="h-3 w-3 text-brand-dark-blue/70" />
          <span className="text-xs text-brand-dark-blue/70"><Trans>Video:</Trans></span>
          {uploadStatus.videoUploadStatus === 'completed' ? (
            <div className="flex items-center gap-1">
              <CheckCircle className="h-3 w-3 text-brand-success" />
              <span className="text-xs text-brand-success font-medium"><Trans>Uploaded</Trans></span>
            </div>
          ) : uploadStatus.videoUploadStatus === 'uploading' ? (
            <div className="flex items-center gap-1">
              <Clock className="h-3 w-3 text-brand-gold animate-spin" />
              <span className="text-xs text-brand-gold font-medium"><Trans>Uploading...</Trans></span>
            </div>
          ) : uploadStatus.videoUploadStatus === 'failed' ? (
            <div className="flex items-center gap-1">
              <AlertCircle className="h-3 w-3 text-brand-error" />
              <span className="text-xs text-brand-error font-medium"><Trans>Failed</Trans></span>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <span className="text-xs text-brand-dark-blue/50 font-medium"><Trans>Audio-only</Trans></span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 pt-2 border-t border-brand-dark-blue/10">
        <span className="text-xs text-brand-dark-blue/70"><Trans>Speech Analysis:</Trans></span>
        <div className="flex items-center gap-1">
                <CheckCircle className="h-3 w-3 text-brand-success" />
                <span className="text-xs text-brand-success font-medium"><Trans>Complete</Trans></span>
              </div>
        </div>

        {/* Audio Analysis Status */}
        {uploadStatus.hasAudio && (
          <div className="flex items-center gap-2 pt-2 border-t border-brand-dark-blue/10">
            <span className="text-xs text-brand-dark-blue/70"><Trans>Audio Analysis:</Trans></span>
            {uploadStatus.audioAnalysisStatus === 'completed' ? (
              <div className="flex items-center gap-1">
                <CheckCircle className="h-3 w-3 text-brand-success" />
                <span className="text-xs text-brand-success font-medium"><Trans>Complete</Trans></span>
              </div>
            ) : uploadStatus.audioAnalysisStatus === 'processing' ? (
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3 text-brand-gold animate-spin" />
                <span className="text-xs text-brand-gold font-medium"><Trans>Processing...</Trans></span>
              </div>
            ) : uploadStatus.audioAnalysisStatus === 'failed' ? (
              <div className="flex items-center gap-1">
                <AlertCircle className="h-3 w-3 text-brand-error" />
                <span className="text-xs text-brand-error font-medium"><Trans>Failed</Trans></span>
              </div>
            ) : uploadStatus.audioAnalysisStatus === 'pending' ? (
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3 text-brand-dark-blue/50" />
                <span className="text-xs text-brand-dark-blue/50 font-medium"><Trans>Pending</Trans></span>
              </div>
            ) : (
              <span className="text-xs text-brand-dark-blue/50 font-medium"><Trans>Not available</Trans></span>
            )}
          </div>
        )}

        {/* Video Analysis Status */}
        {uploadStatus.hasVideo && (
          <div className="flex items-center gap-2 pt-2 border-t border-brand-dark-blue/10">
            <span className="text-xs text-brand-dark-blue/70"><Trans>Video Analysis:</Trans></span>
            {uploadStatus.videoAnalysisStatus === 'completed' ? (
              <div className="flex items-center gap-1">
                <CheckCircle className="h-3 w-3 text-brand-success" />
                <span className="text-xs text-brand-success font-medium"><Trans>Complete</Trans></span>
              </div>
            ) : uploadStatus.videoAnalysisStatus === 'processing' ? (
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3 text-brand-gold animate-spin" />
                <span className="text-xs text-brand-gold font-medium"><Trans>Processing...</Trans></span>
              </div>
            ) : uploadStatus.videoAnalysisStatus === 'failed' ? (
              <div className="flex items-center gap-1">
                <AlertCircle className="h-3 w-3 text-brand-error" />
                <span className="text-xs text-brand-error font-medium"><Trans>Failed</Trans></span>
              </div>
            ) : uploadStatus.videoAnalysisStatus === 'pending' ? (
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3 text-brand-dark-blue/50" />
                <span className="text-xs text-brand-dark-blue/50 font-medium"><Trans>Pending</Trans></span>
              </div>
            ) : (
              <span className="text-xs text-brand-dark-blue/50 font-medium"><Trans>Not available</Trans></span>
            )}
          </div>
        )}

        {/* File count summary */}
        <div className="text-xs text-brand-dark-blue/50 mt-2 pt-2 border-t border-brand-dark-blue/10">
          <Plural value={uploadStatus.totalFiles} one="# file" other="# files" />
          {uploadStatus.videoFileCount > 0 && ` • ${uploadStatus.videoFileCount} `}<Trans>video</Trans>
          {uploadStatus.audioFileCount > 0 && ` • ${uploadStatus.audioFileCount} `}<Trans>audio</Trans>
        </div>
      </div>
    );
  };

  return (
    <div ref={containerRef} style={{ minHeight: styles.height }}>
      <div
        ref={contentRef}
        style={
          isFixed
            ? {
                position: 'fixed',
                top: offsetTop,
                left: styles.left,
                width: styles.width,
                zIndex: 20,
              }
            : undefined
        }
      >
            {children}
        {/* {process.env.NODE_ENV === 'development' && <UploadStatusDisplay />} */}
        <SidebarMetrics analysisData={analysisData} isAnalyzing={isAnalyzing} />
    
      </div>
    </div>
  );
}
